/**
 * ReferencesSection Component
 * Design: "Light with Dark Accents"
 * White cards on #F8F9FC, colored category headers, slate text
 */
import { Award, Building2, GraduationCap, Landmark, ShieldCheck } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const categoryIcons = [Building2, Landmark, GraduationCap];
const categoryColors = ["blue", "emerald", "blue"];
const educationUspIcons = [Award, ShieldCheck, GraduationCap];
const educationUspIconStyles = [
  "border-blue-200 bg-blue-50 text-blue-600",
  "border-emerald-200 bg-emerald-50 text-emerald-600",
  "border-violet-200 bg-violet-50 text-violet-600",
];

const clientsDataDE = [
  {
    id: "corporate",
    clients: [
      "Fertility Center Berlin", "Tutkun Clinics", "Procter & Gamble", "Volkswagen AG",
      "Allianz Versicherungs AG", "Commerzbank", "Axel Springer AG", "Museum of Modern Art, New York",
      "freenet AG", "Franke Holding", "Berlin State Opera", "klarmobil",
      "S-Markt und Mehrwert", "Verband Deutscher Bürgschaftsbanken",
      "Mittelbrandenburgische Sparkasse", "Sparkasse Dortmund", "Sparkasse Barnim", "Sparkasse Ravensburg",
      "Sparkasse Mainfranken Würzburg", "Sparkasse Fulda", "Stadtsparkasse Bad Pyrmont", "Sparkasse Elbe-Elster",
      "Saale-Sparkasse", "Sparkasse Günzburg-Krumbach", "Sparkasse Steinfurt", "Sparkasse Langen-Seligenstadt",
      "Sparkasse Unstrut-Hainich", "Sparkasse Ostprignitz-Ruppin", "Sparkasse Schlüchtern",
    ],
  },
  {
    id: "public",
    clients: [
      "Bundesministerium der Verteidigung (BMVg)",
      "Auswärtiges Amt (AA)",
      "NATO – Ständige Vertretung (Belgien)",
      "Deutsche Botschaft Washington (USA)",
      "Deutsche Botschaft Paris (Frankreich)",
      "Deutsche Botschaft Rom (Italien)",
      "Deutsche Botschaft Oslo (Norwegen)",
      "Deutsche Botschaft Sofia (Bulgarien)",
      "Deutsche Botschaft Reykjavík (Island)",
      "Deutsche Botschaft Kyjiw (Ukraine)",
      "Deutsche Botschaft Tirana (Albanien)",
      "Deutsche Botschaft Minsk (Belarus)",
      "Deutsche Botschaft Straßburg (Frankreich)",
      "Deutsche Botschaft La Paz (Bolivien)",
      "Deutsche Botschaft Port-au-Prince (Haiti)",
      "Deutsche Botschaft Guatemala-Stadt (Guatemala)",
      "Deutsche Botschaft Bogotá (Kolumbien)",
      "Deutsche Botschaft Havanna (Kuba)",
      "Deutsche Botschaft Asunción (Paraguay)",
      "Deutsche Botschaft Montevideo (Uruguay)",
      "Deutsche Botschaft Buenos Aires (Argentinien)",
      "Deutsche Botschaft Astana (Kasachstan)",
      "Deutsche Botschaft Hanoi (Vietnam)",
      "Deutsches Zentrum Kairo (Ägypten)",
      "Deutsche Botschaft Windhoek (Namibia)",
      "Generalkonsulat Nowosibirsk (Russland)",
      "Generalkonsulat Jekaterinburg (Russland)",
      "CDU",
      "Bündnis 90/Die Grünen",
      "Die Linke",
    ],
  },
  {
    id: "education",
    clients: [
      "Industrie- & Handelskammer (IHK)",
      "TÜV",
      "Beuth Hochschule für Technik Berlin",
      "PC College",
    ],
  },
];

const clientsDataEN = [
  {
    id: "corporate",
    clients: [
      "Fertility Center Berlin", "Tutkun Clinics", "Procter & Gamble", "Volkswagen AG",
      "Allianz Insurance AG", "Commerzbank", "Axel Springer AG", "Museum of Modern Art, New York",
      "freenet AG", "Franke Holding", "Berlin State Opera", "klarmobil",
      "S-Markt und Mehrwert", "Association of German Guarantee Banks",
      "Mittelbrandenburgische Sparkasse", "Sparkasse Dortmund", "Sparkasse Barnim", "Sparkasse Ravensburg",
      "Sparkasse Mainfranken Würzburg", "Sparkasse Fulda", "Stadtsparkasse Bad Pyrmont", "Sparkasse Elbe-Elster",
      "Saale-Sparkasse", "Sparkasse Günzburg-Krumbach", "Sparkasse Steinfurt", "Sparkasse Langen-Seligenstadt",
      "Sparkasse Unstrut-Hainich", "Sparkasse Ostprignitz-Ruppin", "Sparkasse Schlüchtern",
    ],
  },
  {
    id: "public",
    clients: [
      "Federal Ministry of Defence (BMVg)",
      "Ministry of Foreign Affairs (AA)",
      "NATO – Permanent Representation (Belgium)",
      "German Embassy Washington (USA)",
      "German Embassy Paris (France)",
      "German Embassy Rome (Italy)",
      "German Embassy Oslo (Norway)",
      "German Embassy Sofia (Bulgaria)",
      "German Embassy Reykjavík (Iceland)",
      "German Embassy Kyiv (Ukraine)",
      "German Embassy Tirana (Albania)",
      "German Embassy Minsk (Belarus)",
      "German Embassy Strasbourg (France)",
      "German Embassy La Paz (Bolivia)",
      "German Embassy Port-au-Prince (Haiti)",
      "German Embassy Guatemala City (Guatemala)",
      "German Embassy Bogotá (Colombia)",
      "German Embassy Havana (Cuba)",
      "German Embassy Asunción (Paraguay)",
      "German Embassy Montevideo (Uruguay)",
      "German Embassy Buenos Aires (Argentina)",
      "German Embassy Astana (Kazakhstan)",
      "German Embassy Hanoi (Vietnam)",
      "German Centre Cairo (Egypt)",
      "German Embassy Windhoek (Namibia)",
      "Consulate General Novosibirsk (Russia)",
      "Consulate General Yekaterinburg (Russia)",
      "CDU",
      "Alliance 90/The Greens",
      "The Left",
    ],
  },
  {
    id: "education",
    clients: [
      "Chamber of Commerce & Industry (IHK)",
      "TÜV",
      "Beuth University of Applied Sciences Berlin",
      "PC College",
    ],
  },
];

const colorMap = {
  blue: {
    header: "border-blue-200 bg-blue-50",
    icon: "text-blue-600 bg-blue-100",
    label: "text-blue-700",
    badge: "bg-blue-100 text-blue-700 border border-blue-200",
    card: "border-slate-200 bg-white hover:border-blue-200 hover:bg-blue-50",
    note: "bg-blue-50 border-blue-100 text-slate-500",
  },
  emerald: {
    header: "border-emerald-200 bg-emerald-50",
    icon: "text-emerald-600 bg-emerald-100",
    label: "text-emerald-700",
    badge: "bg-emerald-100 text-emerald-700 border border-emerald-200",
    card: "border-slate-200 bg-white hover:border-emerald-200 hover:bg-emerald-50",
    note: "bg-emerald-50 border-emerald-100 text-slate-500",
  },
};

export default function ReferencesSection() {
  const { t, language } = useLanguage();

  const clientsData = language === "en" ? clientsDataEN : clientsDataDE;

  return (
    <section id="references" className="pt-12 pb-24 lg:pt-16 lg:pb-32 relative" style={{ background: "#F8F9FC" }}>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mb-12">
          <span className="text-emerald-700 text-sm font-semibold tracking-widest uppercase mb-4 block font-sora">{t.references.sectionLabel}</span>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h2 className="font-sora font-black text-3xl sm:text-4xl lg:text-5xl text-slate-900 leading-tight">
              {t.references.headline.split("\n")[0]}<br />
              <span className="text-emerald-700">{t.references.headline.split("\n")[1]}</span>
            </h2>
            <p className="text-slate-700 max-w-md font-inter leading-relaxed">
              {t.references.subtext}
            </p>
          </div>
        </div>

        {/* All categories expanded and stacked */}
        <div className="space-y-6">
          {t.references.categories.map((cat, i) => {
            const Icon = categoryIcons[i];
            const color = categoryColors[i] as keyof typeof colorMap;
            const colors = colorMap[color];
            const data = clientsData[i];

            return (
              <div
                key={cat.id}
                className="rounded-2xl border border-slate-200 overflow-hidden shadow-sm"
              >
                {/* Category Header */}
                <div className={`flex items-center gap-3 px-6 py-4 border-b ${colors.header}`}>
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${colors.icon}`}>
                    <Icon size={18} />
                  </div>
                  <span className={`font-sora font-bold text-lg ${colors.label}`}>{cat.label}</span>
                  <span className={`ml-auto text-xs px-2.5 py-1 rounded-full font-sora font-semibold ${colors.badge}`}>
                    {data.clients.length}
                  </span>
                </div>

                {/* Clients Grid */}
                <div className="p-5 bg-white">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5">
                    {data.clients.map((client) => (
                      <div
                        key={client}
                        className={`rounded-lg px-3 py-2.5 border transition-all duration-200 ${colors.card}`}
                      >
                        <span className="text-sm font-sora font-medium text-slate-700 leading-tight block">
                          {client}
                        </span>
                      </div>
                    ))}
                  </div>

                  {cat.note && (
                    <div className={`mt-4 px-4 py-3 rounded-xl border ${colors.note}`}>
                      <p className="text-sm font-inter">{cat.note}</p>
                    </div>
                  )}

                  {cat.id === "education" && (
                    <div className={`mt-5 overflow-hidden rounded-xl border ${colors.note}`}>
                      <div className="border-b border-blue-100 bg-blue-50/70 px-4 py-3 sm:px-5">
                        <p className="font-sora text-sm font-bold text-slate-800">
                          {language === "de" ? "Lehre mit Praxisbezug" : "Teaching with practical relevance"}
                        </p>
                      </div>
                      <div className="grid gap-px bg-blue-100 md:grid-cols-3">
                        {t.references.educationUsps.map((usp, uspIndex) => {
                          const UspIcon = educationUspIcons[uspIndex];
                          return (
                            <div key={usp.institution} className="bg-white px-4 py-4 sm:px-5">
                              <div className="flex items-center gap-3">
                                <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border ${educationUspIconStyles[uspIndex]}`}>
                                  <UspIcon size={20} strokeWidth={2.1} aria-hidden="true" />
                                </div>
                                <span className="inline-flex rounded-md bg-blue-600 px-2.5 py-1 font-mono-custom text-[10px] font-bold tracking-[0.08em] text-white">
                                  {usp.institution}
                                </span>
                              </div>
                              <p className="mt-3 text-sm leading-relaxed text-slate-700 font-inter">{usp.text}</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
